import { useId } from '@fluentui/react-components';
import { DrawerHeaderTitle, EmptyState, Menu24Filled, SvgEmptyStateReportsLarge } from '@galaxy-react/core';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LRDrawer from 'src/Shared/Components/Drawer';
import { GlobalMenuItem } from './GlobalMenuItem';
import {
  ActionButton,
  ActionButtonWrapper,
  StyledDivider,
  StyledDrawerBody,
  StyledDrawerHeader,
  StyledIconButton,
  StyledMenu,
  StyledMenuGrid,
  StyledMenuList,
  StyledMenuListNoPad,
  StyledSubmenu,
  StyledSubtitle,
  StyledTitle,
} from './global-menu.styled';

import { useNavigate } from 'react-router-dom';
import { PreviewCard } from './PreviewCard';
// import { getFavouriteReportsWithData, getWithFavouritedMenuData } from '../Menu/MenuSlice';
import { useSelector } from 'react-redux';
import { UseGetFavouriteReports, UseGetMenuWithFavourite } from 'src/Shared/Hooks/FavouriteReportHooks';
import { getCurrentPathNodes } from 'src/Shared/Utilities/Menu';
import { getCurrentReport } from '../Menu/ReportSlice';
import { IMenuItem, IReportItem } from '../Menu/menuModels';

export function GlobalMenu() {
  const menuId = useId('menu');
  const submenuId = useId('submenu');
  const submenuItemId = useId('submenuItem');
  const previewId = useId('preview');
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const [selectedMenu, setSelectedMenu] = useState<string | undefined>();
  const [currentSubMenu, setCurrentSubMenu] = useState<IMenuItem[] | undefined>([]);
  const [currentSubMenuItem, setCurrentSubMenuItem] = useState<IReportItem | undefined>(undefined);

  const originMenuData = UseGetMenuWithFavourite();
  const currentReport = useSelector(getCurrentReport);
  const favouriteReports = UseGetFavouriteReports();

  const menuData = useMemo(() => {
    if (favouriteReports?.length) {
      const favouriteCate: IMenuItem = {
        Name: 'MyFavouriteReports',
        Title: t('My Favourite Reports'),
        SubMenuItems: favouriteReports,
        MenuDescription: t('Access all the reports that you have starred and added to your favourites.'),
      };
      return [favouriteCate, ...originMenuData];
    } else {
      return originMenuData;
    }
  }, [originMenuData, favouriteReports]);

  const handleSelectItem = useCallback(
    (index: number) => {
      setSelectedMenu(menuData[index].Title);
      setCurrentSubMenu(menuData[index].SubMenuItems);
      const firstSubMenuItem = menuData[index].SubMenuItems?.[0];
      setCurrentSubMenuItem(firstSubMenuItem?.SubMenuItems ? firstSubMenuItem.SubMenuItems?.[0] : firstSubMenuItem);
    },
    [menuData],
  );
  useEffect(() => {
    if (!menuData?.length) {
      return;
    }
    if (currentReport) {
      const pathNodes = getCurrentPathNodes(currentReport);
      const firstMenu = menuData.find((item) => item.Name === pathNodes[0]?.Name);
      if (firstMenu) {
        setSelectedMenu(firstMenu.Title);
        setCurrentSubMenu(firstMenu.SubMenuItems);
        setCurrentSubMenuItem(currentReport);
        return;
      }
    }

    handleSelectItem(0);
  }, [menuData, handleSelectItem, currentReport]);

  const handleCloseMenu = (reportItem: IReportItem) => {
    drawerRef.current?.close();
    if (reportItem.Path) {
      navigate(reportItem.Path);
    }
  };

  const handleSelectPreview = (menu: IReportItem) => {
    setCurrentSubMenuItem(menu);
  };

  const handleSearchClick = () => {
    drawerRef.current?.close();
    document.getElementById('reports-global-search')?.click();
  };

  return (
    <LRDrawer
      ref={drawerRef}
      style={{ width: '1200px' }}
      trigger={<StyledIconButton icon={<Menu24Filled />} appearance="transparent" />}
    >
      <StyledDrawerHeader>
        <DrawerHeaderTitle>
          <StyledTitle variant="title">{t('Reports')}</StyledTitle>
        </DrawerHeaderTitle>
      </StyledDrawerHeader>
      <StyledDrawerBody>
        <StyledMenuGrid>
          <StyledMenu>
            <StyledMenuList>
              {menuData.map((globalMenuItem, index) => (
                <div key={`${menuId} + ${globalMenuItem.Title}`}>
                  <GlobalMenuItem
                    title={globalMenuItem.Title}
                    onClick={() => handleSelectItem(index)}
                    description={globalMenuItem.MenuDescription}
                    isSelected={selectedMenu === globalMenuItem.Title}
                    comingSoon={!globalMenuItem?.SubMenuItems?.length}
                  ></GlobalMenuItem>
                  {globalMenuItem.Name === 'MyFavouriteReports' && <StyledDivider />}
                </div>
              ))}
            </StyledMenuList>
            <ActionButtonWrapper>
              <ActionButton onClick={handleSearchClick}>{t('Search Reports')}</ActionButton>
            </ActionButtonWrapper>
          </StyledMenu>

          <StyledSubmenu>
            {currentSubMenu?.map((subMenuItem, subIndex) => (
              <div key={`${submenuId} + ${subMenuItem.Name}`}>
                <StyledMenuList>
                  {subMenuItem.SubMenuItems ? (
                    <>
                      <StyledSubtitle variant="subtitle" style={{ paddingTop: subIndex !== 0 ? 6 : 2 }}>
                        {subMenuItem.Title}
                      </StyledSubtitle>
                      <StyledMenuListNoPad>
                        {subMenuItem.SubMenuItems.map((item) => (
                          <GlobalMenuItem
                            title={item.Title}
                            Path={item.Path}
                            key={`${submenuItemId} + ${item.ReportId}`}
                            onClick={() => handleSelectPreview(item)}
                            isSelected={currentSubMenuItem?.Title === item.Title}
                            isSubMenuItem
                          ></GlobalMenuItem>
                        ))}
                      </StyledMenuListNoPad>
                      <StyledDivider></StyledDivider>
                    </>
                  ) : (
                    <GlobalMenuItem
                      title={subMenuItem.Title}
                      Path={subMenuItem.Path}
                      key={`${submenuItemId} + ${subMenuItem.ReportId}`}
                      onClick={() => handleSelectPreview(subMenuItem)}
                      isSelected={currentSubMenuItem?.Title === subMenuItem.Title}
                      isSubMenuItem
                    ></GlobalMenuItem>
                  )}
                </StyledMenuList>
              </div>
            ))}
            {!currentSubMenu?.length && (
              <EmptyState
                illustration={<SvgEmptyStateReportsLarge />}
                description={t('Reports will be available soon')}
                headline={t('Coming soon')}
              ></EmptyState>
            )}
          </StyledSubmenu>
          {/* TODO: wait for new properties to replace */}
          {currentSubMenuItem && (
            <PreviewCard
              heading={currentSubMenuItem.Title}
              key={`${previewId} + ${currentSubMenuItem.Name}`}
              isFavourite={currentSubMenuItem.IsFavourite}
              // views={currentSubMenuItem.previewViews}
              // chips={currentSubMenuItem.previewChips}
              description={currentSubMenuItem.MenuDescription}
              // lastRun={currentSubMenuItem.previewLastRun}
              // visualised={currentSubMenuItem.previewVisualised}
              action="View"
              handlePreviewAction={() => handleCloseMenu(currentSubMenuItem)}
            />
          )}
        </StyledMenuGrid>
      </StyledDrawerBody>
    </LRDrawer>
  );
}

export default GlobalMenu;
